import { useEffect, useMemo } from "react";

import { useCan } from "components";
import { useAllSelectedLocationIds, useIsAllLocationsSelected, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient, userKey } from "hooks";
import { DMS_CAPABILITIES, ROLES } from "models";
import { isAppointmentInSelectedList } from "modules/Appointments/components/NewAppointment/utils";
import { useAppointmentNotifications, useSelectedDate, useSelectedDates } from "modules/Appointments/hooks";
import { AppointmentsQueryKeys } from "modules/Appointments/queryKeys";
import { ApiInstance } from "utils/Api";
import { updateAppointmentStatusIdentifier } from "utils/appointmentUtils";
import { toISOString } from "utils/dateHelperFunctions";

import type { WebSocketMessageListener } from "components";
import type { TypedBackendQueryKey } from "hooks";
import type { Appointment, AppointmentNote, CarCheck, CustomerCommunication } from "models";

const updateAppointmentStatuses = (appointments: Appointment[]) => appointments.map(updateAppointmentStatusIdentifier);

export const useRealTimeAppointmentsQuery = ({ driveFetch = true }: { driveFetch?: boolean } = {}) => {
  const { isAllLocationsSelected } = useIsAllLocationsSelected();
  const { notifyAppointmentAdded, notifyAppointmentUpdated } = useAppointmentNotifications();

  const { selectedLocation } = useSelectedLocation();
  const selectedDate = useSelectedDate();
  const selectedDates = useSelectedDates();
  const allSelectedLocationIds = useAllSelectedLocationIds();
  const canBrowseAppointments = useCan("browse", "appointments-page");

  const listeners = useMemo(() => {
    if (!selectedLocation?.id) return [];

    const appointmentListeners: WebSocketMessageListener<Appointment>[] = [
      {
        model: "Appointment",
        action: "upsert",
        permanent: true, // TODO: permanent should be set once when passing the list of listeners to useRealTimeQuery, so it can't be missed/added by mistake in the list
        callback: ({ data: appointmentData, user_id }) => {
          const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list) ?? [];

          const newAppointment = updateAppointmentStatusIdentifier(appointmentData);
          const oldAppointmentIdx = appointments.findIndex(a => a.id === newAppointment.id);
          const selectedDate = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDateToList) ?? new Date();
          const selectedDates = typedQueryClient.getQueryData(AppointmentsQueryKeys.selectedDatesToList);
          const listByScheduledRange = Boolean(typedQueryClient.getQueryData(userKey)?.list_appointments_by_scheduled_range);
          const selectedListFilter = { selectedDate, ...(selectedDates ?? {}), listByScheduledRange };

          if (oldAppointmentIdx === -1) {
            if (!isAppointmentInSelectedList({ appointment: newAppointment, ...selectedListFilter })) return;

            notifyAppointmentAdded({ appointment: newAppointment, userId: user_id });
            typedQueryClient.setQueryData(AppointmentsQueryKeys.list, [...appointments, newAppointment]);
            return;
          }

          const oldAppointment = appointments[oldAppointmentIdx];
          const updatedAppointment = updateAppointmentStatusIdentifier({ ...oldAppointment, ...appointmentData });

          if (!isAppointmentInSelectedList({ appointment: updatedAppointment, ...selectedListFilter })) {
            typedQueryClient.setQueryData(AppointmentsQueryKeys.list, appointments.toSpliced(oldAppointmentIdx, 1));
            return;
          }

          notifyAppointmentUpdated({ oldAppointment: updateAppointmentStatusIdentifier(oldAppointment), newAppointment: updatedAppointment, userId: user_id });
          typedQueryClient.setQueryData(AppointmentsQueryKeys.list, appointments.with(oldAppointmentIdx, updatedAppointment));
        }
      }
    ];

    const appointmentNoteListeners: WebSocketMessageListener<AppointmentNote>[] = [
      {
        model: "AppointmentNote",
        action: "upsert",
        permanent: true,
        callback: ({ data: appointmentNote }) => {
          const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list);
          if (!appointments?.length) return;

          const appointmentIdx = appointments.findIndex(appointment => appointment.id === appointmentNote.appointment_id);
          if (appointmentIdx < 0) return;

          const appointment = appointments[appointmentIdx];
          const notes = appointment.notes ?? [];
          const appointmentNoteIdx = notes.findIndex(n => n.id === appointmentNote.id);

          let updatedNotes = [...notes, appointmentNote];
          if (appointmentNoteIdx > -1) updatedNotes = notes.with(appointmentNoteIdx, { ...notes[appointmentNoteIdx], ...appointmentNote });

          typedQueryClient.setQueryData(AppointmentsQueryKeys.list, appointments.with(appointmentIdx, { ...appointment, notes: updatedNotes }));
        }
      },
      {
        model: "AppointmentNote",
        action: "delete",
        permanent: true,
        callback: ({ data: appointmentNote }) => {
          const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list);
          if (!appointments?.length) return;

          const appointmentIdx = appointments.findIndex(a => a.id === appointmentNote.appointment_id);
          const appointment = appointments[appointmentIdx];
          if (appointmentIdx < 0 || !appointment.notes?.length) return;

          const noteIdx = appointment.notes.findIndex(n => n.id === appointmentNote.id);
          if (noteIdx < 0) return;

          typedQueryClient.setQueryData(
            AppointmentsQueryKeys.list,
            appointments.with(appointmentIdx, { ...appointment, notes: appointment.notes.toSpliced(noteIdx, 1) })
          );
        }
      }
    ];

    const customerCommunicationListeners: WebSocketMessageListener<CustomerCommunication>[] = [
      {
        model: "CustomerCommunication",
        action: "upsert",
        permanent: true,
        callback: ({ data: customerCommunication }) => {
          const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list);
          if (!appointments?.length) return;

          const appointmentIdx = appointments.findIndex(a => a.id === customerCommunication.appointment_id);
          if (appointmentIdx < 0) return;

          if (!appointments[appointmentIdx].customer_communication) {
            typedQueryClient.setQueryData(
              AppointmentsQueryKeys.list,
              appointments.with(appointmentIdx, { ...appointments[appointmentIdx], customer_communication: customerCommunication })
            );
            return;
          }

          typedQueryClient.setQueryData(
            AppointmentsQueryKeys.list,
            appointments.with(appointmentIdx, {
              ...appointments[appointmentIdx],
              customer_communication: { ...appointments[appointmentIdx].customer_communication, ...customerCommunication }
            })
          );
        }
      }
    ];

    const checkListeners: WebSocketMessageListener<CarCheck>[] = [
      {
        model: "CarCheck",
        action: "upsert",
        permanent: true,
        callback: ({ data: check }) => {
          const appointments = typedQueryClient.getQueryData(AppointmentsQueryKeys.list);
          if (!appointments?.length) return;

          const appointmentIdx = appointments.findIndex(a => a.id === check.appointment_id);
          if (appointmentIdx < 0) return;

          typedQueryClient.setQueryData(
            AppointmentsQueryKeys.list,
            appointments.with(appointmentIdx, {
              ...appointments[appointmentIdx],
              checklists: (appointments[appointmentIdx].checklists ?? []).concat(check.checklist ? [check.checklist] : [])
            })
          );
        }
      }
    ];

    return [...appointmentListeners, ...appointmentNoteListeners, ...customerCommunicationListeners, ...checkListeners];
  }, [selectedLocation?.id]);

  const fetchAppointments = async ({ baseUrl, endpoint }: TypedBackendQueryKey<Appointment[]>) => {
    const locationIds = { location_ids: isAllLocationsSelected ? allSelectedLocationIds : [selectedLocation?.id] };
    const user = typedQueryClient.getQueryData(userKey);
    const isSelectingDateRange = user?.role_id && ![ROLES.CallCenter, ROLES.WarrantyManager].includes(user.role_id);

    let datePayload;

    if (isSelectingDateRange) {
      const startDate = toISOString(selectedDates.startDate || new Date());
      const endDate = selectedDates.endDate ? toISOString(selectedDates.endDate) : null;

      if (!endDate || selectedDates.startDate === selectedDates.endDate)
        datePayload = { date: startDate, list_by_scheduled_range: Boolean(user.list_appointments_by_scheduled_range) };
      else
        datePayload = {
          date: endDate || startDate,
          since_date: startDate,
          list_by_scheduled_range: Boolean(user.list_appointments_by_scheduled_range)
        };
    } else {
      const date = toISOString(selectedDate || new Date());
      datePayload = { date, list_by_scheduled_range: Boolean(user?.list_appointments_by_scheduled_range) };
    }

    const response = await ApiInstance.post(endpoint, { ...locationIds, ...datePayload }, baseUrl);
    return response?.data?.appointments || [];
  };

  const query = useRealTimeTypedQuery({
    queryKey: AppointmentsQueryKeys.list,
    queryFn: fetchAppointments,
    select: updateAppointmentStatuses,
    listeners,
    enabled: false
  });

  // TODO: what's going on with this useEffect dependency, they don't match what's used, and now that a range is forced selectedDate and selectedDates.startDate should not be needed ?
  useEffect(() => {
    if (!driveFetch || !canBrowseAppointments) return;

    const user = typedQueryClient.getQueryData(userKey);
    const isSelectingDateRange = user?.role_id && ![ROLES.CallCenter, ROLES.WarrantyManager].includes(user.role_id);

    if (!selectedLocation?.id) return;
    if (user?.list_appointments_by_scheduled_range && !selectedLocation.dms_capability_ids?.includes(DMS_CAPABILITIES.ScheduledInOut)) return;
    if (isSelectingDateRange && !selectedDates.endDate) return;

    typedQueryClient.resetQueries(AppointmentsQueryKeys.list);
    // TODO: there is a problem with doing a refetch in a useEffect like this: if this hook is called multiple times in the page, each call has it's own useEffect, which means it will call refetch multiple times, we either have to look for a solution or add query.isFetching to the dependency/condition calling refetch to workaround that
    query.refetch(); // seems this is needed, but according to documentation if the query is active then resetQueries should already trigger the refetch, to be checked
  }, [canBrowseAppointments, selectedLocation?.id, selectedDate, selectedDates.startDate, selectedDates.endDate]);

  return query;
};

export const refetchAppointments = () => typedQueryClient.invalidateQueries(AppointmentsQueryKeys.list);
